import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useSetting, formatDeadline } from '@/hooks/useSetting';
import { eventInfo } from '@/mocks/landing-data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface TeamMember {
  fullName: string;
  email: string;
  contactNumber: string;
  department: string;
  yearOfStudy: string;
  courseType: string;
}

interface Domain {
  id: string;
  name: string;
  description: string;
}

interface ProblemStatement {
  id: string;
  title: string;
  description: string | null;
  difficulty: string | null;
}

interface TeamData {
  teamName: string;
  collegeName: string;
  city: string;
  members: TeamMember[];
  hasFacultyMentor: boolean;
  facultyName: string;
  facultyDesignation: string;
  facultyDepartment: string;
  facultyCollege: string;
  facultyPhone: string;
  facultyEmail: string;
  password: string;
  confirmPassword: string;
  selectedDomain: string;
  selectedDomainName: string;
  selectedProblemStatement: string;
  selectedProblemStatementTitle: string;
}

const initialMember = (): TeamMember => ({
  fullName: '',
  email: '',
  contactNumber: '',
  department: '',
  yearOfStudy: '',
  courseType: '',
});

const steps = [
  { num: 1, label: 'Domain & PS' },
  { num: 2, label: 'Team Info' },
  { num: 3, label: 'Team Members' },
  { num: 4, label: 'Faculty Mentor' },
  { num: 5, label: 'Account Setup' },
  { num: 6, label: 'Review & Submit' },
];

const years = ['I', 'II', 'III', 'IV'];

export default function Register() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [problemStatements, setProblemStatements] = useState<ProblemStatement[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [loadingPS, setLoadingPS] = useState(false);
  const [formData, setFormData] = useState<TeamData>({
    teamName: '',
    collegeName: '',
    city: '',
    members: [{ ...initialMember() }, initialMember(), initialMember()],
    hasFacultyMentor: false,
    facultyName: '',
    facultyDesignation: '',
    facultyDepartment: '',
    facultyCollege: '',
    facultyPhone: '',
    facultyEmail: '',
    password: '',
    confirmPassword: '',
    selectedDomain: '',
    selectedDomainName: '',
    selectedProblemStatement: '',
    selectedProblemStatementTitle: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [registrationId, setRegistrationId] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const eventName = useSetting('event_name', eventInfo.name);
  const deadlineRaw = useSetting('registration_deadline', '');
  const displayDeadline = deadlineRaw ? formatDeadline(deadlineRaw) : 'September 1, 2026';

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    try {
      const { data } = await supabase.from('domains').select('id, name, description').eq('status', true).order('sort_order').order('name');
      setDomains(data || []);
    } catch {
      // silent
    } finally {
      setLoadingDomains(false);
    }
  };

  const loadProblemStatements = async (domainId: string) => {
    setLoadingPS(true);
    try {
      const { data } = await supabase
        .from('problem_statements')
        .select('id, title, description, difficulty')
        .eq('domain_id', domainId)
        .eq('is_published', true)
        .eq('is_enabled', true)
        .order('title');
      setProblemStatements(data || []);
    } catch {
      setProblemStatements([]);
    } finally {
      setLoadingPS(false);
    }
  };

  const updateField = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const updateMember = (index: number, field: keyof TeamMember, value: string) => {
    setFormData((prev) => {
      const members = [...prev.members];
      members[index] = { ...members[index], [field]: value };
      return { ...prev, members };
    });
    const key = `member${index}_${field}`;
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const addMember = () => {
    if (formData.members.length < 4) {
      setFormData((prev) => ({ ...prev, members: [...prev.members, initialMember()] }));
    }
  };

  const removeMember = (index: number) => {
    if (formData.members.length > 3) {
      setFormData((prev) => ({
        ...prev,
        members: prev.members.filter((_, i) => i !== index),
      }));
    }
  };

  const handleDomainChange = (domainId: string) => {
    const domain = domains.find((d) => d.id === domainId);
    setFormData((prev) => ({
      ...prev,
      selectedDomain: domainId,
      selectedDomainName: domain?.name || '',
      selectedProblemStatement: '',
      selectedProblemStatementTitle: '',
    }));
    setProblemStatements([]);
    if (domainId) loadProblemStatements(domainId);
  };

  const handlePSSelect = (psId: string) => {
    const ps = problemStatements.find((p) => p.id === psId);
    setFormData((prev) => ({
      ...prev,
      selectedProblemStatement: psId,
      selectedProblemStatementTitle: ps?.title || '',
    }));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.selectedDomain) newErrors.selectedDomain = 'Please select a domain';
      if (!formData.selectedProblemStatement) newErrors.selectedProblemStatement = 'Please select a problem statement';
    }

    if (step === 2) {
      if (!formData.teamName.trim()) newErrors.teamName = 'Team name is required';
      if (!formData.collegeName.trim()) newErrors.collegeName = 'College name is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
    }

    if (step === 3) {
      formData.members.forEach((m, i) => {
        if (!m.fullName.trim()) newErrors[`member${i}_fullName`] = 'Name is required';
        if (!m.email.trim()) newErrors[`member${i}_email`] = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(m.email)) newErrors[`member${i}_email`] = 'Invalid email format';
        if (!m.contactNumber.trim()) newErrors[`member${i}_contactNumber`] = 'Contact is required';
        else if (!/^\d{10}$/.test(m.contactNumber.replace(/[\s-]/g, ''))) newErrors[`member${i}_contactNumber`] = 'Enter valid 10-digit number';
        if (!m.department.trim()) newErrors[`member${i}_department`] = 'Department is required';
        if (!m.yearOfStudy) newErrors[`member${i}_yearOfStudy`] = 'Year is required';
        if (!m.courseType) newErrors[`member${i}_courseType`] = 'Course type is required';
      });
    }

    if (step === 4 && formData.hasFacultyMentor) {
      if (!formData.facultyName.trim()) newErrors.facultyName = 'Name is required';
      if (!formData.facultyDesignation.trim()) newErrors.facultyDesignation = 'Designation is required';
      if (!formData.facultyDepartment.trim()) newErrors.facultyDepartment = 'Department is required';
      if (!formData.facultyPhone.trim()) newErrors.facultyPhone = 'Contact is required';
      if (!formData.facultyEmail.trim()) newErrors.facultyEmail = 'Email is required';
    }

    if (step === 5) {
      if (!formData.password) newErrors.password = 'Password is required';
      else if (formData.password.length < 8) newErrors.password = 'Minimum 8 characters';
      else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/.test(formData.password))
        newErrors.password = 'Needs uppercase, lowercase, number & special char';
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
      if (!consentGiven) newErrors.consent = 'You must agree to the consent to proceed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 6));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;

    setSubmitting(true);
    setSubmitError('');
    console.log('[Register] Starting registration submission...');

    const leaderEmail = formData.members[0].email.trim().toLowerCase();

    try {
      const payload = {
        team_name: formData.teamName.trim(),
        college_name: formData.collegeName.trim(),
        city: formData.city.trim(),
        team_email: leaderEmail,
        password: formData.password,
        members: formData.members.map((m) => ({
          full_name: m.fullName.trim(),
          email: m.email.trim().toLowerCase(),
          contact_number: m.contactNumber.trim(),
          department: m.department.trim(),
          year_of_study: m.yearOfStudy,
          course_type: m.courseType,
        })),
        has_faculty_mentor: formData.hasFacultyMentor,
        faculty_name: formData.facultyName.trim(),
        faculty_designation: formData.facultyDesignation.trim(),
        faculty_department: formData.facultyDepartment.trim(),
        faculty_college: formData.collegeName.trim(),
        faculty_phone: formData.facultyPhone.trim(),
        faculty_email: formData.facultyEmail.trim(),
        domain_id: formData.selectedDomain,
        domain_name: formData.selectedDomainName,
        problem_statement_id: formData.selectedProblemStatement,
        problem_statement_title: formData.selectedProblemStatementTitle,
        consent_given: consentGiven,
      };

      console.log('[Register] Sending payload to edge function');

      const functionUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/register-team`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(payload),
      });

      let result: Record<string, unknown> = {};
      const responseText = await response.text();

      try {
        result = JSON.parse(responseText);
      } catch {
        setSubmitError(`Server returned an unexpected response (status ${response.status}). Please try again.`);
        setSubmitting(false);
        return;
      }

      if (!response.ok) {
        const serverError = (result.error as string) || (result.message as string) || 'Registration failed';
        setSubmitError(serverError);
        setSubmitting(false);
        return;
      }

      if (!result.success) {
        setSubmitError((result.error as string) || 'Registration failed for an unknown reason.');
        setSubmitting(false);
        return;
      }

      setRegistrationId(result.registration_id as string);
      setSubmitted(true);
      console.log('[Register] Registration successful!');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Network error';
      setSubmitError(`Unable to reach the server: ${errMsg}. Please check your internet connection.`);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field: string) =>
    `w-full px-4 py-2.5 rounded-lg border text-sm transition-colors duration-200 outline-none ${
      errors[field]
        ? 'border-red-300 bg-red-50/30 focus:border-red-500 focus:ring-1 focus:ring-red-200'
        : 'border-background-200 bg-background-50 focus:border-primary-400 focus:ring-1 focus:ring-primary-200'
    } text-foreground-900 placeholder:text-foreground-400`;

  const selectedDomainObj = domains.find((d) => d.id === formData.selectedDomain);
  const selectedPSObj = problemStatements.find((p) => p.id === formData.selectedProblemStatement);

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-20 pb-16 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          {submitted ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-6">
                <i className="ri-check-line text-3xl text-accent-600"></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Registration Submitted!
              </h1>
              <p className="text-foreground-600 mb-2">
                Your Team ID: <strong className="text-foreground-900">{registrationId}</strong>
              </p>
              <p className="text-foreground-500 mb-6 max-w-md mx-auto text-sm">
                Your account is ready! Sign in with <strong>{formData.members[0].email.trim().toLowerCase()}</strong> and complete the payment to confirm your registration.
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <button
                  onClick={() => navigate('/payment')}
                  className="px-8 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-base transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Proceed to Payment
                </button>
                <Link
                  to="/login"
                  className="px-8 py-3 rounded-lg border border-background-300 text-foreground-700 font-medium text-base hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Sign In
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/* Step Indicator */}
              <div className="mb-10">
                <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-alert-line text-amber-600 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Registration Deadline: {displayDeadline}</p>
                    <p className="text-xs text-amber-600 mt-1">
                      Teams must complete registration and payment before the deadline to gain access to the problem statements. Late registrations will not be accepted.
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  {steps.map((step, i) => (
                    <div key={step.num} className="flex items-center flex-1">
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                            currentStep === step.num
                              ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                              : currentStep > step.num
                              ? 'bg-accent-500 text-background-50 dark:text-foreground-950'
                              : 'bg-background-200 text-foreground-400'
                          }`}
                        >
                          {currentStep > step.num ? <i className="ri-check-line"></i> : step.num}
                        </div>
                        <span
                          className={`text-xs mt-1.5 hidden sm:block whitespace-nowrap ${
                            currentStep >= step.num ? 'text-foreground-700 font-medium' : 'text-foreground-400'
                          }`}
                        >
                          {step.label}
                        </span>
                      </div>
                      {i < steps.length - 1 && (
                        <div
                          className={`flex-1 h-0.5 mx-2 mt-[-16px] transition-colors duration-300 ${
                            currentStep > step.num ? 'bg-accent-300' : 'bg-background-200'
                          }`}
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 1: Domain & Problem Statement */}
              {currentStep === 1 && (
                <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-1">Domain & Problem Statement</h2>
                  <p className="text-sm text-foreground-500 mb-6">Select your problem domain and preferred problem statement</p>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-foreground-700 mb-2">Select Domain *</label>
                    {loadingDomains ? (
                      <div className="flex justify-center py-8"><div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div></div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {domains.map((domain) => (
                          <button
                            key={domain.id}
                            type="button"
                            onClick={() => handleDomainChange(domain.id)}
                            className={`text-left p-4 rounded-xl border transition-all cursor-pointer ${
                              formData.selectedDomain === domain.id
                                ? 'border-primary-400 bg-primary-50/50'
                                : 'border-background-200/70 hover:border-background-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                formData.selectedDomain === domain.id ? 'bg-primary-100' : 'bg-background-100'
                              }`}>
                                <i className={`ri-stack-line text-sm ${formData.selectedDomain === domain.id ? 'text-primary-600' : 'text-foreground-400'}`}></i>
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-foreground-900">{domain.name}</p>
                                <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{domain.description}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {errors.selectedDomain && <p className="text-xs text-red-500 mt-2">{errors.selectedDomain}</p>}
                  </div>

                  {formData.selectedDomain && (
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-2">Select Problem Statement *</label>
                      {loadingPS ? (
                        <div className="flex justify-center py-8"><div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div></div>
                      ) : problemStatements.length === 0 ? (
                        <p className="text-sm text-foreground-400 p-4 bg-background-100 rounded-xl">
                          No published problem statements available for this domain yet. Please check back later or contact the organizers.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {problemStatements.map((ps) => (
                            <button
                              key={ps.id}
                              type="button"
                              onClick={() => handlePSSelect(ps.id)}
                              className={`w-full text-left p-4 rounded-xl border transition-all cursor-pointer ${
                                formData.selectedProblemStatement === ps.id
                                  ? 'border-primary-400 bg-primary-50/50'
                                  : 'border-background-200/70 hover:border-background-300'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-sm font-semibold text-foreground-900">{ps.title}</p>
                                  {ps.description && (
                                    <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{ps.description}</p>
                                  )}
                                </div>
                                {ps.difficulty && (
                                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                                    ps.difficulty === 'Advanced' ? 'bg-red-100 text-red-700' : ps.difficulty === 'Intermediate' ? 'bg-amber-100 text-amber-700' : 'bg-accent-100 text-accent-700'
                                  }`}>{ps.difficulty}</span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {errors.selectedProblemStatement && <p className="text-xs text-red-500 mt-2">{errors.selectedProblemStatement}</p>}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Team Info */}
              {currentStep === 2 && (
                <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-1">Team Information</h2>
                  <p className="text-sm text-foreground-500 mb-6">Basic team details. The Team Leader&apos;s email will be used for login.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">Team Name *</label>
                      <input type="text" className={inputClass('teamName')} placeholder="Enter team name" value={formData.teamName} onChange={(e) => updateField('teamName', e.target.value)} />
                      {errors.teamName && <p className="text-xs text-red-500 mt-1">{errors.teamName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">College Name *</label>
                      <input type="text" className={inputClass('collegeName')} placeholder="Enter college name" value={formData.collegeName} onChange={(e) => updateField('collegeName', e.target.value)} />
                      {errors.collegeName && <p className="text-xs text-red-500 mt-1">{errors.collegeName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">City *</label>
                      <input type="text" className={inputClass('city')} placeholder="Enter city" value={formData.city} onChange={(e) => updateField('city', e.target.value)} />
                      {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Team Members */}
              {currentStep === 3 && (
                <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="font-heading text-xl font-bold text-foreground-950">Team Members</h2>
                      <p className="text-sm text-foreground-500">Add 3-4 participants. Participant 1 is the Team Leader.</p>
                    </div>
                    {formData.members.length < 4 && (
                      <button onClick={addMember} className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
                        <i className="ri-add-line mr-1"></i> Add Member
                      </button>
                    )}
                  </div>

                  {formData.members.map((member, idx) => (
                    <div key={idx} className="mb-6 p-5 rounded-xl border border-background-200/70 bg-background-50">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-semibold text-foreground-800 text-sm">
                          Participant {idx + 1} {idx === 0 && <span className="text-primary-600 text-xs ml-1">(Team Leader)</span>}
                        </h3>
                        {formData.members.length > 3 && idx >= 3 && (
                          <button onClick={() => removeMember(idx)} className="text-red-400 hover:text-red-600 transition-colors cursor-pointer">
                            <i className="ri-close-circle-line text-lg"></i>
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">Full Name *</label>
                          <input type="text" className={inputClass(`member${idx}_fullName`)} placeholder="Full name" value={member.fullName} onChange={(e) => updateMember(idx, 'fullName', e.target.value)} />
                          {errors[`member${idx}_fullName`] && <p className="text-xs text-red-500 mt-0.5">{errors[`member${idx}_fullName`]}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">Email ID *</label>
                          <input type="email" className={inputClass(`member${idx}_email`)} placeholder="email@example.com" value={member.email} onChange={(e) => updateMember(idx, 'email', e.target.value)} />
                          {errors[`member${idx}_email`] && <p className="text-xs text-red-500 mt-0.5">{errors[`member${idx}_email`]}</p>}
                          {idx === 0 && <p className="text-xs text-primary-500 mt-0.5">This email will be used for login</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">Contact Number *</label>
                          <input type="tel" className={inputClass(`member${idx}_contactNumber`)} placeholder="10-digit" value={member.contactNumber} onChange={(e) => updateMember(idx, 'contactNumber', e.target.value)} />
                          {errors[`member${idx}_contactNumber`] && <p className="text-xs text-red-500 mt-0.5">{errors[`member${idx}_contactNumber`]}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">Department *</label>
                          <input type="text" className={inputClass(`member${idx}_department`)} placeholder="e.g. Computer Science" value={member.department} onChange={(e) => updateMember(idx, 'department', e.target.value)} />
                          {errors[`member${idx}_department`] && <p className="text-xs text-red-500 mt-0.5">{errors[`member${idx}_department`]}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">Year of Study *</label>
                          <div className="flex gap-2 flex-wrap">
                            {years.map((y) => (
                              <button
                                key={y}
                                type="button"
                                onClick={() => updateMember(idx, 'yearOfStudy', y)}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                                  member.yearOfStudy === y
                                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                                }`}
                              >
                                {y}
                              </button>
                            ))}
                          </div>
                          {errors[`member${idx}_yearOfStudy`] && <p className="text-xs text-red-500 mt-0.5">{errors[`member${idx}_yearOfStudy`]}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">Course Type *</label>
                          <div className="flex gap-3">
                            {['UG', 'PG'].map((ct) => (
                              <label key={ct} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`courseType_${idx}`}
                                  value={ct}
                                  checked={member.courseType === ct}
                                  onChange={() => updateMember(idx, 'courseType', ct)}
                                  className="w-4 h-4 accent-primary-500"
                                />
                                <span className="text-sm text-foreground-700">{ct}</span>
                              </label>
                            ))}
                          </div>
                          {errors[`member${idx}_courseType`] && <p className="text-xs text-red-500 mt-0.5">{errors[`member${idx}_courseType`]}</p>}
                        </div>
                      </div>
                    </div>
                  ))}

                  {formData.members.length < 3 && (
                    <p className="text-xs text-amber-600 bg-amber-50/50 border border-amber-200 rounded-lg p-3">
                      Minimum 3 team members required. Add at least {3 - formData.members.length} more.
                    </p>
                  )}
                </div>
              )}

              {/* Step 4: Faculty Mentor */}
              {currentStep === 4 && (
                <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-1">Faculty Mentor</h2>
                  <p className="text-sm text-foreground-500 mb-6">Will a faculty mentor accompany this project?</p>

                  <div className="flex gap-3 mb-6">
                    {['Yes', 'No'].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => updateField('hasFacultyMentor', opt === 'Yes')}
                        className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                          (opt === 'Yes' && formData.hasFacultyMentor) || (opt === 'No' && !formData.hasFacultyMentor)
                            ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>

                  {formData.hasFacultyMentor && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in">
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">Faculty Name *</label>
                        <input type="text" className={inputClass('facultyName')} placeholder="Faculty name" value={formData.facultyName} onChange={(e) => updateField('facultyName', e.target.value)} />
                        {errors.facultyName && <p className="text-xs text-red-500 mt-1">{errors.facultyName}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">Designation *</label>
                        <input type="text" className={inputClass('facultyDesignation')} placeholder="e.g. Assistant Professor" value={formData.facultyDesignation} onChange={(e) => updateField('facultyDesignation', e.target.value)} />
                        {errors.facultyDesignation && <p className="text-xs text-red-500 mt-1">{errors.facultyDesignation}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">Department *</label>
                        <input type="text" className={inputClass('facultyDepartment')} placeholder="Department" value={formData.facultyDepartment} onChange={(e) => updateField('facultyDepartment', e.target.value)} />
                        {errors.facultyDepartment && <p className="text-xs text-red-500 mt-1">{errors.facultyDepartment}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">Contact Number *</label>
                        <input type="tel" className={inputClass('facultyPhone')} placeholder="10-digit" value={formData.facultyPhone} onChange={(e) => updateField('facultyPhone', e.target.value)} />
                        {errors.facultyPhone && <p className="text-xs text-red-500 mt-1">{errors.facultyPhone}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">Email ID *</label>
                        <input type="email" className={inputClass('facultyEmail')} placeholder="faculty@example.com" value={formData.facultyEmail} onChange={(e) => updateField('facultyEmail', e.target.value)} />
                        {errors.facultyEmail && <p className="text-xs text-red-500 mt-1">{errors.facultyEmail}</p>}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 5: Account Setup */}
              {currentStep === 5 && (
                <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-1">Account Setup</h2>
                  <p className="text-sm text-foreground-500 mb-6">Create login credentials for the team leader account</p>

                  <div className="max-w-md space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">Email (Login Username)</label>
                      <input type="email" className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-100 text-foreground-500 text-sm cursor-not-allowed" value={formData.members[0].email.trim().toLowerCase() || '(enter team leader email in Step 3)'} disabled />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">Password *</label>
                      <input type="password" className={inputClass('password')} placeholder="Create password" value={formData.password} onChange={(e) => updateField('password', e.target.value)} />
                      {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
                      <div className="mt-2 flex gap-1 flex-wrap">
                        {[
                          { test: formData.password.length >= 8, label: '8+ chars' },
                          { test: /[A-Z]/.test(formData.password), label: 'Uppercase' },
                          { test: /[a-z]/.test(formData.password), label: 'Lowercase' },
                          { test: /\d/.test(formData.password), label: 'Number' },
                          { test: /[!@#$%^&*]/.test(formData.password), label: 'Special char' },
                        ].map((item) => (
                          <span
                            key={item.label}
                            className={`text-xs px-2 py-0.5 rounded ${
                              formData.password
                                ? item.test
                                  ? 'bg-accent-100 text-accent-700'
                                  : 'bg-background-200 text-foreground-400'
                                : 'bg-background-100 text-foreground-400'
                            }`}
                          >
                            {item.label}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">Confirm Password *</label>
                      <input type="password" className={inputClass('confirmPassword')} placeholder="Confirm password" value={formData.confirmPassword} onChange={(e) => updateField('confirmPassword', e.target.value)} />
                      {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
                    </div>

                    <label className="flex items-start gap-3 p-4 rounded-xl border border-background-200/70 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={consentGiven}
                        onChange={(e) => setConsentGiven(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded accent-primary-500 flex-shrink-0"
                      />
                      <span className="text-xs text-foreground-600 leading-relaxed">
                        I consent to provide my personal information for the purpose of participating in {eventName} Hackathon. My information will be used only for registration, communication, evaluation, and event management in accordance with the Privacy Policy.
                      </span>
                    </label>
                    {errors.consent && <p className="text-xs text-red-500">{errors.consent}</p>}
                  </div>
                </div>
              )}

              {/* Step 6: Review */}
              {currentStep === 6 && (
                <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-1">Review & Submit</h2>
                  <p className="text-sm text-foreground-500 mb-6">Please verify all details before submitting</p>

                  {submitError && (
                    <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                      {submitError}
                    </div>
                  )}

                  <div className="space-y-6">
                    <div className="p-4 rounded-xl bg-background-100">
                      <h3 className="font-heading font-semibold text-foreground-800 text-sm mb-3">Domain & Problem Statement</h3>
                      <div className="text-sm space-y-1">
                        <p><span className="text-foreground-400">Domain:</span> <span className="text-foreground-800 font-medium">{formData.selectedDomainName || '-'}</span></p>
                        <p><span className="text-foreground-400">Problem Statement:</span> <span className="text-foreground-800 font-medium">{formData.selectedProblemStatementTitle || '-'}</span></p>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-background-100">
                      <h3 className="font-heading font-semibold text-foreground-800 text-sm mb-3">Team Information</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {[
                          ['Team Name', formData.teamName],
                          ['College', formData.collegeName],
                          ['City', formData.city],
                          ['Login Email', formData.members[0].email.trim().toLowerCase()],
                        ].map(([label, value]) => (
                          <div key={label}>
                            <span className="text-foreground-400">{label}:</span>{' '}
                            <span className="text-foreground-800 font-medium">{value || '-'}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-background-100">
                      <h3 className="font-heading font-semibold text-foreground-800 text-sm mb-3">Team Members ({formData.members.length})</h3>
                      {formData.members.map((m, i) => (
                        <div key={i} className="text-sm py-1.5 border-b border-background-200 last:border-0">
                          <span className="font-medium text-foreground-800">{i + 1}. {m.fullName}</span>
                          <span className="text-foreground-400 ml-2">({m.department}, {m.courseType} — {m.yearOfStudy} yr) - {m.email}</span>
                          {i === 0 && <span className="text-primary-600 text-xs ml-2">[Team Leader]</span>}
                        </div>
                      ))}
                    </div>

                    <div className="p-4 rounded-xl bg-background-100">
                      <h3 className="font-heading font-semibold text-foreground-800 text-sm mb-2">Faculty Mentor</h3>
                      {formData.hasFacultyMentor ? (
                        <div className="grid grid-cols-2 gap-1 text-sm">
                          {[
                            ['Name', formData.facultyName],
                            ['Designation', formData.facultyDesignation],
                            ['Department', formData.facultyDepartment],
                            ['Phone', formData.facultyPhone],
                            ['Email', formData.facultyEmail],
                          ].map(([l, v]) => (
                            <div key={l}><span className="text-foreground-400">{l}:</span> <span className="text-foreground-800 font-medium">{v || '-'}</span></div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-foreground-400">No faculty mentor</p>
                      )}
                    </div>

                    <label className="flex items-start gap-3 p-4 rounded-xl border border-background-200/70 cursor-pointer">
                      <input type="checkbox" className="mt-0.5 w-4 h-4 rounded accent-primary-500" required />
                      <span className="text-sm text-foreground-600">
                        I confirm that all information provided is correct. I agree to the <a href="#" className="text-primary-600 underline">Hackathon Rules</a> and <a href="#" className="text-primary-600 underline">Privacy Policy</a>. I understand that registration is not complete until payment is processed.
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8">
                {currentStep > 1 ? (
                  <button onClick={handleBack} className="px-6 py-2.5 rounded-lg border border-background-300 text-foreground-700 font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-arrow-left-line mr-1.5"></i> Back
                  </button>
                ) : (
                  <div></div>
                )}
                {currentStep < 6 ? (
                  <button onClick={handleNext} className="px-6 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">
                    Next <i className="ri-arrow-right-line ml-1.5"></i>
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="px-8 py-2.5 rounded-lg bg-accent-500 hover:bg-accent-600 disabled:bg-accent-400 disabled:cursor-not-allowed text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    {submitting && <div className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin"></div>}
                    {submitting ? 'Submitting...' : 'Submit Registration'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}